import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserData } from "@/lib/api";
import { useStrengths } from "@/hooks/use-strengths";
import { Navigation } from "@/components/Navigation";
import { Dashboard } from "@/components/Dashboard";
import { DnaBackground } from "@/components/DnaBackground";
import StrengthsChart from "@/components/StrengthsChart";
import PDFExport from "@/components/PDFExport";
import ChatModal from "@/components/ChatModal";
import UploadReportSection from "@/components/UploadReportSection";
import { QuizProgressDashboard } from "@/components/QuizProgressDashboard";
import StrengthCardGenerator from "@/components/StrengthCard";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, BarChart3, FileDown, Target, Share2, Users, Trophy, Map } from "lucide-react";
import { socketService } from "@/services/socketService";
import UserDirectoryList from "@/components/UserDirectoryList";
import ThemeLibraryGrid from "@/components/ThemeLibraryGrid";

export default function UserDashboard() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [user, setUser] = useState<any>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [userStrengths, setUserStrengths] = useState<any[]>([]);
  const [topDomains, setTopDomains] = useState<Array<{ domain: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getMyTop10, getMyByDomain } = useStrengths();
  const tabParam = searchParams.get("tab");
  const allowedTabs = ["overview", "category", "explorer", "charts", "progress", "upload", "export", "sharecard"];
  const activeTab = allowedTabs.includes(tabParam || "") ? tabParam! : "overview";

  const handleTabChange = (value: string) => {
    if (value === "teamMap") {
      navigate("/team-strengths-map");
      return;
    }

    if (value === "overview") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  useEffect(() => {
    const userData = getUserData();
    
    if (!userData) {
      navigate("/auth");
    } else if (userData.role === 'admin') {
      // Redirect admin to admin page
      navigate("/admin");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      loadUserStrengths();
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      socketService.connect(user.id);
    }

    return () => {
      socketService.disconnect();
    };
  }, [user]);  

  const loadUserStrengths = async () => {
    if (!user?.name) return;
    
    setLoading(true);
    try {
      const result = await getMyTop10();
      
      if (result.success && result.data) {
        // Map the API response to the format expected by components
        const mappedStrengths = result.data.rankings?.map((ranking: any) => ({
          name: ranking.strength.name,
          nameAr: ranking.strength.nameAr,
          rank: ranking.rank,
          domain: ranking.strength.domain,
        })) || [];
        
        setUserStrengths(mappedStrengths);
      }

      // Fetch domain distribution from API and derive top 2
      const domainResult = await getMyByDomain();
      if (domainResult.success && domainResult.data?.strengthsByDomain) {
        const sorted = Object.entries(domainResult.data.strengthsByDomain as Record<string, any[]>)
          .map(([domain, items]) => ({ domain, count: items.length }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 2);
        setTopDomains(sorted);
      }
    } catch (error) {
      console.error('Error loading user strengths:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <DnaBackground />
      {t('userDashboard.loading')}
    </div>;
  }

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />

      <div className="pt-20 px-4 pb-10 text-start sm:px-6 sm:pt-24 sm:pb-12">
        <div className="container mx-auto max-w-7xl min-w-0">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 sm:text-4xl">{t('userDashboard.myDashboard')}</h1>
            <p className="text-base text-muted-foreground sm:text-xl">
              {t('userDashboard.subtitle')}
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="min-w-0 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
            <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
              <TabsList className="w-max min-w-max bg-card border border-border">
              <TabsTrigger value="overview" className="gap-2">
                <Target className="w-4 h-4" />
                {t('userDashboard.overview')}
              </TabsTrigger>
              <TabsTrigger value="category" className="gap-2">
                <Users className="w-4 h-4" />
                {t('userDashboard.categoryTab')}
              </TabsTrigger>
              <TabsTrigger value="explorer" className="gap-2">
                <Trophy className="w-4 h-4" />
                {t('userDashboard.explorerTab')}
              </TabsTrigger>
              <TabsTrigger value="teamMap" className="gap-2">
                <Map className="w-4 h-4" />
                {t('userDashboard.teamMapTab')}
              </TabsTrigger>
              <TabsTrigger value="charts" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                {t('userDashboard.analytics')}
              </TabsTrigger>
              <TabsTrigger value="upload" className="gap-2">
                <Upload className="w-4 h-4" />
                {t('userDashboard.uploadReport')}
              </TabsTrigger>
              <TabsTrigger value="export" className="gap-2">
                <FileDown className="w-4 h-4" />
                {t('userDashboard.export')}
              </TabsTrigger>
              <TabsTrigger value="sharecard" className="gap-2">
                <Share2 className="w-4 h-4" />
                {t('userDashboard.shareCard')}
              </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="min-w-0">
              <Dashboard />
            </TabsContent>

            <TabsContent value="category" className="min-w-0">
              <UserDirectoryList />
            </TabsContent>

            <TabsContent value="explorer" className="min-w-0">
              <ThemeLibraryGrid />
            </TabsContent>

            <TabsContent value="charts" className="min-w-0">
              {userStrengths.length > 0 ? (
                <StrengthsChart strengths={userStrengths} showTop={10} />
              ) : (
                <Card className="p-6 bg-card border-border">
                  <p className="text-center text-muted-foreground">{t('userDashboard.noDataAvailable')}</p>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="progress" className="min-w-0">
              <QuizProgressDashboard />
            </TabsContent>

            <TabsContent value="upload" className="min-w-0">
              <UploadReportSection 
                onDataExtracted={(data) => {
                  console.log('Data extracted:', data);
                  loadUserStrengths();
                }}
              />
            </TabsContent>

            <TabsContent value="export" className="min-w-0">
              <Card className="p-4 bg-card border-border sm:p-6">
                <h2 className="text-xl font-bold mb-4 sm:text-2xl">{t('userDashboard.exportTitle')}</h2>
                <p className="text-muted-foreground mb-6">
                  {t('userDashboard.exportDesc')}
                </p>
                {userStrengths.length > 0 ? (
                  <PDFExport
                    userName={user.name || user.email || "User"}
                    strengths={userStrengths}
                    topDomains={[...new Set(userStrengths.map(s => s.domain))]}
                    completionDate={new Date().toISOString()}
                  />
                ) : (
                  <p className="text-center text-muted-foreground">{t('userDashboard.noDataAvailable')}</p>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="sharecard" className="min-w-0">
              <Card className="p-4 bg-card border-border sm:p-6">
                <h2 className="text-xl font-bold mb-2 sm:text-2xl">{t('userDashboard.shareCardTitle')}</h2>
                <p className="text-muted-foreground mb-6">
                  {t('userDashboard.shareCardDesc')}
                </p>
                <StrengthCardGenerator
                  userName={user.name || user.email || "User"}
                  strengths={userStrengths}
                  topDomains={topDomains.length > 0 ? topDomains : undefined}
                  userId={user.id || user._id || user.userId}
                />
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Chat Modal */}
      <ChatModal 
        open={chatModalOpen}
        onOpenChange={setChatModalOpen}
        isAdmin={false}
      />
    </div>
  );
}
